<template>
  <div class="custom-impact-slider relative" @mouseenter="pauseAutoplay" @mouseleave="resumeAutoplay">
    <div class="pt-8 sm:pt-8 md:pt-12 lg:pt-20 pb-8 sm:pb-8 md:pb-12">
      <div class="slider-container w-full relative">
        <transition-group name="fade" tag="div" class="relative">
          <div 
            v-for="(slide, index) in slides" 
            :key="slide.id" 
            :class="['slide w-full', { 'active': currentSlide === index }]"
          >
            <div class="flex flex-col lg:flex-row items-center">
              <div class="w-full lg:w-6/12 mb-6 sm:mb-8 lg:mb-0 lg:pr-12 flex flex-col justify-center">
                <span class="uppercase font-bold mb-1 text-blue text-xl sm:text-2xl md:text-3xl lg:text-4xl">{{ slide.title }}</span>
                <span class="font-bold mb-1 text-black text-xl sm:text-2xl md:text-3xl lg:text-4xl">{{ slide.number }}</span>
                <span class="font-bold mb-4 sm:mb-6 text-black text-base sm:text-lg lg:text-xl">{{ slide.subtitle }}</span>
                <p v-html="slide.description" class="text-sm sm:text-base md:text-lg lg:text-xl"></p>
              </div>
              <div class="w-full lg:w-6/12 flex justify-center items-center">
                <div class="inline-block max-w-full">
                  <img :src="slide.image" alt="" class="max-w-full h-auto object-contain max-h-[300px] sm:max-h-[400px] md:max-h-[500px] lg:max-h-none">
                </div>
              </div>
            </div>
          </div>
        </transition-group>
      </div>
    </div>
    <div v-if="slides.length > 1" class="mt-4 sm:mt-6 flex justify-center lg:justify-end space-x-2 sm:space-x-4 mb-6">
      <button @click="prevSlide" class="impact-prev text-pink-500 hover:text-pink-600">
        <span class="fa-stack fa-lg sm:fa-xl">
          <i class="fal fa-circle fa-stack-2x"></i>
          <i class="fas fa-chevron-left fa-stack-1x"></i>
        </span>
      </button>
      <button @click="nextSlide" class="impact-next text-pink-500 hover:text-pink-600">
        <span class="fa-stack fa-lg sm:fa-xl">
          <i class="fal fa-circle fa-stack-2x"></i>
          <i class="fas fa-chevron-right fa-stack-1x"></i>
        </span>
      </button>
    </div>
  </div>
</template>

<script>
export default {
  name: 'CustomImpactSlider',
  props: {
    slides: {
      type: Array,
      required: true
    },
    autoplay: {
      type: Boolean,
      default: true
    },
  },
  data() {
    return {
      currentSlide: 0,
      autoplayInterval: null,
      isAutoplayPaused: false
    }
  },
  mounted() {
    if (this.autoplay) {
      this.startAutoplay();
    }
  },
  beforeUnmount() {
    this.stopAutoplay();
  },
  methods: {
    nextSlide() {
      const nextIndex = (this.currentSlide + 1) % this.slides.length;
      this.goToSlide(nextIndex);
    },
    prevSlide() {
      const prevIndex = (this.currentSlide - 1 + this.slides.length) % this.slides.length;
      this.goToSlide(prevIndex);
    },
    goToSlide(index) {
      this.currentSlide = index;
      this.$emit('slide-changed', index);
      this.resetAutoplay();
    },
    startAutoplay() {
      if (!this.isAutoplayPaused) {
        this.stopAutoplay(); // Clear any existing interval
        this.autoplayInterval = setInterval(() => {
          this.nextSlide();
        }, 10000);
      }
    },
    stopAutoplay() {
      clearInterval(this.autoplayInterval);
      this.autoplayInterval = null;
    },
    pauseAutoplay() {
      this.isAutoplayPaused = true;
      this.stopAutoplay();
    },
    resumeAutoplay() {
      this.isAutoplayPaused = false;
      if (this.autoplay) {
        this.startAutoplay();
      }
    },
    resetAutoplay() {
      this.stopAutoplay();
      this.startAutoplay();
    },
  },
}
</script>

<style scoped>
.custom-impact-slider {
  position: relative;
  width: 100%;
}

.slider-container {
  position: relative;
  overflow: hidden;
}

.slide {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  opacity: 0;
  transition: opacity 0.5s ease;
  pointer-events: none;
}

.slide.active {
  position: relative;
  opacity: 1;
  pointer-events: auto;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.5s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

.fade-enter-to,
.fade-leave-from {
  opacity: 1;
}

@media (max-width: 640px) {
  .custom-impact-slider {
    padding-left: 1rem;
    padding-right: 1rem;
  }
}
</style>